import { DetailedCallRecordDirection } from './detailed-call-records-report.resource';
import { CallRecordSearchType } from '../pstn-call-record-finder/pstn-call-record-query-editor-field/pstn-call-record-query-editor-field.component';

export enum CallDirection {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
  OTHER = 'OTHER',
}

export interface CallRecordLike {
  callerNumber?: string | null;
  calleeNumber?: string | null;
  callDirection?: CallDirection | null;
}

export interface UserIdentity {
  userPrincipalName: string;
  displayName: string;
}

export interface ToFromCells {
  from: string;
  fromSubtext: string;
  to: string;
  toSubtext: string;
}

export class CallToFromUtil {
  private static readonly EMPTY = '';

  static computeCells(args: {
    record: CallRecordLike;
    selectedDirection: DetailedCallRecordDirection;
    searchBy: CallRecordSearchType;
    user: UserIdentity;
    telephoneNumberFilter: (value: string) => string;
  }): ToFromCells {
    const { record, user, telephoneNumberFilter } = args;
    const searchBy = args.searchBy;
    const rowDirection = this._resolveRowDirection(args.selectedDirection, record.callDirection);
    const callerNumber = telephoneNumberFilter(record.callerNumber ?? this.EMPTY);
    const calleeNumber = telephoneNumberFilter(record.calleeNumber ?? this.EMPTY);

    switch (rowDirection) {
      case CallDirection.INBOUND: {
        return this.inbound({ searchBy, user, callerNumber, calleeNumber });
      }
      case CallDirection.OUTBOUND: {
        return this.outbound({ searchBy, user, callerNumber, calleeNumber });
      }
      case CallDirection.OTHER:
      default: {
        return {
          from: callerNumber,
          fromSubtext: this.EMPTY,
          to: calleeNumber,
          toSubtext: this.EMPTY,
        };
      }
    }
  }

  private static inbound(args: {
    searchBy: CallRecordSearchType;
    user: UserIdentity;
    callerNumber: string;
    calleeNumber: string;
  }): ToFromCells {
    const { searchBy, user, callerNumber, calleeNumber } = args;
    const from = callerNumber;
    switch (searchBy) {
      case CallRecordSearchType.USER_PRINCIPAL_NAME: {
        return { from, fromSubtext: this.EMPTY, to: user.userPrincipalName, toSubtext: calleeNumber };
      }
      case CallRecordSearchType.DISPLAY_NAME: {
        return { from, fromSubtext: this.EMPTY, to: user.displayName, toSubtext: calleeNumber };
      }
      case CallRecordSearchType.NUMBER:
      default: {
        return { from, fromSubtext: this.EMPTY, to: calleeNumber, toSubtext: user.displayName };
      }
    }
  }

  private static outbound(args: {
    searchBy: CallRecordSearchType;
    user: UserIdentity;
    callerNumber: string;
    calleeNumber: string;
  }): ToFromCells {
    const { searchBy, user, callerNumber, calleeNumber } = args;
    const to = calleeNumber;
    switch (searchBy) {
      case CallRecordSearchType.USER_PRINCIPAL_NAME:
        return { from: user.userPrincipalName, fromSubtext: callerNumber, to, toSubtext: this.EMPTY };
      case CallRecordSearchType.DISPLAY_NAME:
        return { from: user.displayName, fromSubtext: callerNumber, to, toSubtext: this.EMPTY };
      case CallRecordSearchType.NUMBER:
      default:
        return { from: callerNumber, fromSubtext: user.displayName, to, toSubtext: this.EMPTY };
    }
  }

  private static _resolveRowDirection(
    selectedDirection: DetailedCallRecordDirection,
    recordDirection?: CallDirection | null,
  ): CallDirection {
    if (selectedDirection === DetailedCallRecordDirection.INBOUND) {
      return CallDirection.INBOUND;
    }
    if (selectedDirection === DetailedCallRecordDirection.OUTBOUND) {
      return CallDirection.OUTBOUND;
    }
    return recordDirection ?? CallDirection.OTHER;
  }
}
